import { useCallback, useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Archive, Download, RotateCcw, Loader2, Clock, Users, AlertTriangle } from "lucide-react";

function fmtSize(bytes) {
  if (!bytes) return "—";
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(2)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
}

const TIPO_LABELS = {
  single: "Delete único",
  bulk: "Delete em lote",
};

export default function BackupBrowserPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null);
  const [toRestore, setToRestore] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/backups/pre-delete", { params: { limit: 100 } });
      setItems(r.data.items);
      setTotal(r.data.total);
    } catch (e) {
      logger.error("Falha ao carregar backups", e);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const baixar = (b) => {
    window.open(`${API}/backups/pre-delete/${b.id}/download`, "_blank");
  };

  const confirmarRestore = async () => {
    if (!toRestore) return;
    setRestoring(toRestore.id);
    try {
      const r = await api.post(`/backups/pre-delete/${toRestore.id}/restaurar`);
      toast.success(`Restauração concluída. ${JSON.stringify(r.data.restaurados)}`);
      setToRestore(null);
    } catch (e) {
      logger.error("Falha ao restaurar backup", e);
      toast.error(e.response?.data?.detail || "Falha ao restaurar");
    } finally { setRestoring(null); }
  };

  return (
    <div className="space-y-6" data-testid="backup-browser-page">
      <div>
        <h1 className="text-3xl font-black text-[#083CA6] flex items-center gap-3">
          <Archive className="w-8 h-8" /> Arquivo de Backups (pré-delete)
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Backups automáticos gerados antes de exclusões de inscrições (singular ou em lote).
          Use para auditoria ou para restaurar uma inscrição excluída indevidamente.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Backups disponíveis</CardTitle>
          <CardDescription>{total.toLocaleString("pt-BR")} registros · ordenados do mais recente ao mais antigo</CardDescription>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Carregando...</div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              Nenhum backup pré-delete registrado ainda. Backups são criados automaticamente
              quando uma inscrição é excluída.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-3 font-semibold">Data/Hora</th>
                  <th className="text-left p-3 font-semibold">Tipo</th>
                  <th className="text-left p-3 font-semibold">Protocolo(s)</th>
                  <th className="text-left p-3 font-semibold">Qtd</th>
                  <th className="text-left p-3 font-semibold">Tamanho</th>
                  <th className="text-left p-3 font-semibold">Excluído por</th>
                  <th className="text-center p-3 font-semibold">Ações</th>
                </tr>
              </thead>
              <tbody>
                {items.map((b, idx) => (
                  <tr key={b.id} className={idx % 2 ? "bg-slate-50" : ""} data-testid={`backup-row-${b.id}`}>
                    <td className="p-3 whitespace-nowrap font-mono text-xs">
                      <Clock className="w-3 h-3 inline mr-1 text-slate-400" />
                      {new Date(b.created_at).toLocaleString("pt-BR")}
                    </td>
                    <td className="p-3">
                      <Badge className={b.tipo === "bulk" ? "bg-purple-100 text-purple-800" : "bg-blue-100 text-blue-800"}>
                        {TIPO_LABELS[b.tipo] || b.tipo}
                      </Badge>
                    </td>
                    <td className="p-3 text-xs font-mono max-w-sm truncate" title={(b.protocolos || [b.protocolo]).join(", ")}>
                      {b.protocolo || (b.protocolos && b.protocolos.slice(0, 2).join(", ")) || "—"}
                      {b.protocolos && b.protocolos.length > 2 && ` +${b.protocolos.length - 2}`}
                    </td>
                    <td className="p-3">
                      <Users className="w-3.5 h-3.5 inline mr-1 text-slate-400" />{b.qtd_inscricoes}
                    </td>
                    <td className="p-3 text-xs">{fmtSize(b.size)}</td>
                    <td className="p-3 text-xs">{b.created_by_nome || "—"}</td>
                    <td className="p-3">
                      <div className="flex gap-1 justify-center">
                        <Button size="sm" variant="outline" title="Baixar SQLite"
                                onClick={() => baixar(b)} data-testid={`btn-baixar-${b.id}`}>
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-300 hover:bg-emerald-600 hover:text-white"
                                title="Restaurar inscrições deste backup"
                                onClick={() => setToRestore(b)}
                                data-testid={`btn-restaurar-${b.id}`}
                                disabled={restoring === b.id}>
                          {restoring === b.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!toRestore} onOpenChange={(o) => !o && setToRestore(null)}>
        <DialogContent className="max-w-md" data-testid="dialog-restore-backup">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" /> Restaurar Backup
            </DialogTitle>
            <DialogDescription>
              Esta ação irá reinserir no banco as inscrições que estavam neste backup
              (via upsert pelo ID). Se o CPF ou protocolo já existir, será sobrescrito.
            </DialogDescription>
          </DialogHeader>
          {toRestore && (
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm space-y-1">
              <div><strong>Tipo:</strong> {TIPO_LABELS[toRestore.tipo] || toRestore.tipo}</div>
              <div><strong>Quantidade:</strong> {toRestore.qtd_inscricoes} inscrição(ões)</div>
              <div><strong>Data:</strong> {new Date(toRestore.created_at).toLocaleString("pt-BR")}</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setToRestore(null)}>Cancelar</Button>
            <Button onClick={confirmarRestore} disabled={!!restoring}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="btn-confirm-restore">
              {restoring ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
              Restaurar inscrições
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
